import styled from '@emotion/styled';
import tw from 'twin.macro';
import dayjs from 'dayjs';
import { Popover } from 'antd';
import Calender, { DisabledDatesType } from 'components/molecules/Calender';
import { default as CalenderIcon } from 'assets/svg/Calender';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FilterContextData } from 'contexts/FilterContext';
import { calculateFontSize, getConfig } from 'utils/helperMethods';
import { colorConstants } from 'config/colorConstants';
import { TooltipPlacement } from 'antd/es/tooltip';
import { UserContext } from 'contexts/ConfigContext';
import MonthlyCalenderRange from 'components/molecules/MonthlyCalenderRange';
import QuarterCalenderRange from 'components/molecules/QuarterCalenderRange';

export type DateRangeType = [Date | null, Date | null];

interface Props {
    setStartDate: any;
    setEndDate: any;
    startDate?: string;
    endDate?: string;
    isDisabled?: boolean;
    calenderHeader?: string;
    position?: TooltipPlacement;
    deriveEndFromDurationSeconds?: number;
    /** When true, clear this range and parent state whenever the global filter time period changes. */
    resetOnGlobalTimePeriodChange?: boolean;
}

const scaledWidth = calculateFontSize(window.innerWidth, 13);

const CalenderHeaderDiv = styled.div`
    ${tw`pb-1`};
    font-size: ${scaledWidth}px;
    color: ${colorConstants.fontGrey};
    font-weight: 500;
`;

const CalenderPriceChange: FC<Props> = ({
    setStartDate,
    setEndDate,
    isDisabled,
    startDate,
    endDate,
    calenderHeader,
    position = 'right',
    deriveEndFromDurationSeconds,
    resetOnGlobalTimePeriodChange = false,
}) => {
    const { latestDate, calenderStartDay, currentFrequency, timePeriodSelection } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const isMonthlyGranularity = currentFrequency?.key?.toLowerCase() === 'm';
    const isQuarterGranularity = currentFrequency?.key?.toLowerCase() === 'q';
    const formattedLatestData = dayjs(latestDate, commonConfig?.dateFormat).format('MM/DD/YYYY');
    const latestYear = dayjs(latestDate, commonConfig?.dateFormat).year();
    const [isOpen, setIsOpen] = useState(false);

    /** Only for derived compare: last calendar day pickable as start (latestDate − duration). Otherwise weekly uses `latestDate` as before. */
    const durationSeconds = Number(deriveEndFromDurationSeconds);
    const hasDerivedEndDuration = Number.isFinite(durationSeconds) && durationSeconds > 0;
    const dateFormat = commonConfig?.dateFormat || 'MM/DD/YYYY';

    const latestSelectableStartDate = useMemo(() => {
        if (!latestDate || !hasDerivedEndDuration) return null;

        const latest = dayjs(latestDate, dateFormat);
        if (!latest.isValid()) return null;

        return latest.subtract(durationSeconds, 'second').format(dateFormat);
    }, [latestDate, hasDerivedEndDuration, durationSeconds, dateFormat]);

    const weeklyDisabledEndDate = hasDerivedEndDuration ? latestSelectableStartDate ?? latestDate : latestDate;

    const [tempRange, setTempRange] = useState<any>([
        startDate
            ? isMonthlyGranularity || isQuarterGranularity
                ? dayjs(startDate, commonConfig?.dateFormat)
                : new Date(dayjs(startDate, commonConfig?.dateFormat).format('MM/DD/YYYY'))
            : null,
        endDate
            ? isMonthlyGranularity || isQuarterGranularity
                ? dayjs(endDate, commonConfig?.dateFormat)
                : new Date(dayjs(endDate, commonConfig?.dateFormat).format('MM/DD/YYYY'))
            : null,
    ]);
    const [stateForCalender, setStateForCalender] = useState({ step: 1, dateRange: [null, null] });

    const prevGlobalTimePeriodKeyRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!resetOnGlobalTimePeriodChange) return;
        const key =
            timePeriodSelection == null || timePeriodSelection === ''
                ? ''
                : typeof timePeriodSelection === 'string'
                ? timePeriodSelection
                : JSON.stringify(timePeriodSelection);
        const prev = prevGlobalTimePeriodKeyRef.current;
        prevGlobalTimePeriodKeyRef.current = key;
        if (prev === undefined || prev === key) return;

        setStartDate('');
        setEndDate('');
        setTempRange([null, null]);
        setStateForCalender((p: any) => ({ ...p, dateRange: [null, null] }));
        setIsOpen(false);
    }, [resetOnGlobalTimePeriodChange, timePeriodSelection, setStartDate, setEndDate]);

    /** singleDateSelect stores the pick in range[1]; we keep full [start, end] in tempRange so Set Date drives both setStartDate and setEndDate. */
    const weeklyCalenderDisplayRange: DateRangeType = useMemo(() => {
        if (!hasDerivedEndDuration) return tempRange;
        const start = tempRange[0] ?? tempRange[1];
        if (!start) return [null, null];
        return [null, start];
    }, [tempRange, hasDerivedEndDuration]);

    const dateLabel = useMemo(() => {
        let startLabel = 'Start Date';
        let endLabel = 'End Date';
        const { dateRange } = stateForCalender;
        if (dateRange[0]) {
            startLabel =
                isMonthlyGranularity || isQuarterGranularity
                    ? dateRange[0]
                    : dayjs(new Date(dateRange[0])).format(commonConfig?.dateFormat);
            setStartDate(startLabel);
        }
        if (dateRange[1]) {
            endLabel =
                isMonthlyGranularity || isQuarterGranularity
                    ? dateRange[1]
                    : dayjs(new Date(dateRange[1])).format(commonConfig?.dateFormat);
            setEndDate(endLabel);
        }
        return { startLabel, endLabel };
    }, [stateForCalender]);

    const handleDateChange = (range: DateRangeType) => {
        if (hasDerivedEndDuration && deriveEndFromDurationSeconds && (range[0] || range[1])) {
            const start = range[0] ?? range[1];
            if (start) {
                const end = new Date(start.getTime() + deriveEndFromDurationSeconds * 1000);
                setTempRange([start, end]);
                return;
            }
        }
        setTempRange([...range]);
    };
    const handleOpenChange = (
        newOpen: boolean,
        isClickOutside: boolean = true,
        from: 'header' | 'dateRange' = 'dateRange',
    ) => {
        setIsOpen(newOpen);
        if (!newOpen && !isClickOutside) {
            let savedRange = tempRange;
            if (hasDerivedEndDuration && deriveEndFromDurationSeconds) {
                const start = tempRange[0] ?? tempRange[1];
                if (start) {
                    savedRange = [start, new Date(start.getTime() + deriveEndFromDurationSeconds * 1000)];
                    setTempRange(savedRange);
                }
            }
            setStateForCalender((prev: any) => ({ ...prev, dateRange: savedRange }));
            // const startDateUnix = dayjs(tempRange[0]).add(utcOffset, 'm').subtract(6, 'day').unix();
            // const endDateUnix = dayjs(tempRange[1]).add(utcOffset, 'm').subtract(6, 'day').unix();
        } else if (!newOpen)
            setTempRange([
                startDate
                    ? new Date(dayjs(startDate, commonConfig?.dateFormat).format('MM/DD/YYYY'))
                    : stateForCalender.dateRange?.[0],
                endDate
                    ? new Date(dayjs(endDate, commonConfig?.dateFormat).format('MM/DD/YYYY'))
                    : stateForCalender.dateRange?.[1],
            ]); //if clicked outside dont save the change
    };
    //called when clicked on set date for monthly calender
    const onMonthlyQuarterlySubmit = (startDateUnix: number, endDateUnix: number) => {
        //only set calender state when clicked on set date
        setStateForCalender((prev: any) => ({ ...prev, dateRange: tempRange }));
    };
    const disabledDates: DisabledDatesType = [null, formattedLatestData];
    const calenderDatesConfig = {
        startYear: latestYear === 1970 || !latestYear ? undefined : latestYear,
        disabledDates,
    };

    return (
        <div>
            <CalenderHeaderDiv>{calenderHeader}</CalenderHeaderDiv>
            {isMonthlyGranularity ? (
                <MonthlyCalenderRange
                    disabled={isDisabled}
                    tempRange={tempRange}
                    setTempRange={setTempRange}
                    onDateSubmit={onMonthlyQuarterlySubmit}
                    calenderDatesConfig={calenderDatesConfig}
                />
            ) : isQuarterGranularity ? (
                <QuarterCalenderRange
                    tempRange={tempRange}
                    setTempRange={setTempRange}
                    onDateSubmit={onMonthlyQuarterlySubmit}
                    calenderDatesConfig={calenderDatesConfig}
                />
            ) : (
                <Popover
                    trigger="click"
                    open={isOpen && !isDisabled}
                    onOpenChange={handleOpenChange}
                    placement={position}
                    content={
                        <Calender
                            startYear={latestYear === 1970 || !latestYear ? undefined : latestYear}
                            disabledDates={[null, weeklyDisabledEndDate]}
                            startDay={calenderStartDay}
                            range={weeklyCalenderDisplayRange}
                            singleDateSelect={hasDerivedEndDuration}
                            onChange={handleDateChange}
                            onFooterClick={() => handleOpenChange(false, false)}
                        />
                    }
                >
                    <div className="flex justify-center items-center w-full">
                        <div
                            className="cursor-pointer w-full flex justify-start gap-1 py-1 items-center px-1 border border-purpleFont rounded"
                            style={isDisabled ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                        >
                            <CalenderIcon />
                            <div className="flex w-full justify-center">
                                <span className="text-subHeaderColor pt-[1px] font-xs">
                                    {startDate || dateLabel.startLabel}
                                </span>
                                <span className="text-subHeaderColor mx-1"> - </span>
                                <span className="text-subHeaderColor pt-[1px] font-xs">
                                    {endDate || dateLabel.endLabel}
                                </span>
                            </div>
                        </div>
                    </div>
                </Popover>
            )}
        </div>
    );
};
export default CalenderPriceChange;
